/**
 * Phase 25.6 — Mode-selection landing page.
 *
 * Shown at /welcome and (via redirect from /) to:
 *   - Unauthenticated visitors landing on the root URL.
 *   - Authenticated users who have not yet picked a mode (heuristic:
 *     localStorage `vehicletco_welcome_completed` flag is absent and the
 *     user has no engagement signal — see useAppModeStore.initialize for
 *     how existing users are auto-seeded so they skip this page).
 *
 * Picking a mode:
 *   - Authenticated → PATCH /api/profile with the chosen app_mode,
 *     update useAppModeStore.activeMode, navigate to `/` (scenario list).
 *   - Unauthenticated → write the chosen mode + a pending-apply flag to
 *     localStorage, navigate to `/login`. AuthCallbackPage picks up the
 *     pending flag and PATCHes the profile after the session resolves.
 *
 * Layout: full-page, centered, no app chrome — this is a pre-auth surface.
 */
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useAppModeStore, type AppMode } from '../store/useAppModeStore';

// Inline icon components — avoids pulling in a new dependency for two glyphs.
function CarIcon({ size = 28, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2" />
      <circle cx="6.5" cy="16.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
    </svg>
  );
}

function TruckIcon({ size = 28, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18H9" />
      <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
      <circle cx="17" cy="18" r="2" />
      <circle cx="7" cy="18" r="2" />
    </svg>
  );
}

export const WELCOME_COMPLETED_KEY = 'vehicletco_welcome_completed';
export const PENDING_MODE_KEY = 'vehicletco_pending_mode_apply';
const APP_MODE_KEY = 'vehicletco_app_mode';

function markWelcomeCompleted(mode: AppMode) {
  try {
    localStorage.setItem(WELCOME_COMPLETED_KEY, 'true');
    localStorage.setItem(APP_MODE_KEY, mode);
  } catch {
    // ignore — quota or privacy mode
  }
}

function setPendingModeApply(mode: AppMode) {
  try {
    localStorage.setItem(PENDING_MODE_KEY, mode);
  } catch {
    // ignore — quota or privacy mode
  }
}

type IconComponent = (props: { size?: number; color?: string }) => React.ReactElement;

interface ModeCardProps {
  mode: AppMode;
  icon: IconComponent;
  headline: string;
  body: string;
  badge?: string;
  borderColor: string;
  hoverBg: string;
  onPick: (mode: AppMode) => void;
  disabled: boolean;
}

function ModeCard({
  mode,
  icon,
  headline,
  body,
  badge,
  borderColor,
  hoverBg,
  onPick,
  disabled,
}: ModeCardProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(mode)}
      data-testid={`welcome-card-${mode}`}
      className="welcome-card"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        background: 'var(--tco-surface)',
        border: '1px solid var(--tco-line)',
        borderRadius: 'var(--tco-radius, 8px)',
        padding: '32px 28px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'background 0.2s, box-shadow 0.2s, border-color 0.2s, transform 0.2s',
        minHeight: 280,
        width: '100%',
        // Custom properties consumed by .welcome-card:hover in index.css.
        ['--welcome-hover-bg' as string]: hoverBg,
        ['--welcome-border-color' as string]: borderColor,
      }}
    >
      {badge && (
        <span
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '4px 10px',
            borderRadius: 999,
            background: 'var(--tco-ink)',
            color: 'var(--tco-bg)',
          }}
        >
          {badge}
        </span>
      )}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 12,
          background: hoverBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 20,
          color: borderColor,
        }}
      >
        {icon({ size: 28, color: borderColor })}
      </div>
      <h2
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 24,
          fontWeight: 700,
          color: 'var(--tco-ink)',
          margin: 0,
          marginBottom: 10,
        }}
      >
        {headline}
      </h2>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: 'var(--tco-ink-2)',
          margin: 0,
          marginBottom: 24,
          flex: 1,
        }}
      >
        {body}
      </p>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.04em',
          color: 'var(--tco-ink)',
          borderBottom: `2px solid ${borderColor}`,
          paddingBottom: 2,
        }}
      >
        Get started →
      </span>
    </button>
  );
}

export default function WelcomePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const setMode = useAppModeStore((s) => s.setMode);
  const loading = useAppModeStore((s) => s.loading);

  useEffect(() => {
    document.body.classList.add('tco-ledger');
    return () => {
      document.body.classList.remove('tco-ledger');
    };
  }, []);

  const isAuthenticated = !!user;
  const submitting = loading;

  // Resilience (UX audit P0): while the profile/mode request is in flight the
  // mode cards are disabled. On a cold start that can take ~50s, so surface a
  // bounded "waking up" hint instead of silently-dead buttons.
  const [slowLoad, setSlowLoad] = useState(false);
  useEffect(() => {
    if (!submitting) {
      setSlowLoad(false);
      return;
    }
    const t = window.setTimeout(() => setSlowLoad(true), 6000);
    return () => window.clearTimeout(t);
  }, [submitting]);

  async function handlePick(mode: AppMode) {
    if (submitting) return;
    if (isAuthenticated) {
      // Phase 26.2: setMode will open the fleet-tier gate (and return early
      // without flipping mode) if a free-tier user picks Fleet. Only mark
      // welcome completed + navigate when the mode actually changed —
      // otherwise the next session would read the unauthenticated mode from
      // localStorage and route the user into Fleet without ever firing the
      // gate.
      await setMode(mode, true);
      const gated = useAppModeStore.getState().fleetGateOpen;
      if (gated) {
        // FleetGateModal is mounted in Layout; it doesn't render on /welcome.
        // Bounce the user to '/' so the modal becomes visible and they can
        // redeem (or cancel back to Personal).
        navigate('/', { replace: true });
        return;
      }
      markWelcomeCompleted(mode);
      navigate('/', { replace: true });
    } else {
      // Stash the choice so AuthCallbackPage can PATCH the profile once the
      // user signs in.
      markWelcomeCompleted(mode);
      setPendingModeApply(mode);
      navigate('/login', { replace: true });
    }
  }

  if (!initialized) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--tco-bg)',
          color: 'var(--tco-ink-2)',
          fontFamily: 'var(--tco-font-sans)',
        }}
      >
        Loading…
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--tco-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        fontFamily: 'var(--tco-font-sans)',
      }}
    >
      <div style={{ maxWidth: 960, width: '100%' }}>
        <header style={{ textAlign: 'center', marginBottom: 48 }}>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(36px, 5vw, 52px)',
              fontWeight: 700,
              color: 'var(--tco-ink)',
              margin: 0,
              marginBottom: 12,
              letterSpacing: '-0.01em',
            }}
          >
            VehicleTCO
          </h1>
          <p
            style={{
              fontSize: 'clamp(15px, 1.6vw, 18px)',
              color: 'var(--tco-ink-2)',
              margin: 0,
              maxWidth: 560,
              marginInline: 'auto',
              lineHeight: 1.55,
            }}
          >
            Total cost of ownership, done right.
          </p>
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 24,
          }}
        >
          <ModeCard
            mode="individual"
            icon={CarIcon}
            headline="Personal Vehicle TCO"
            body="Compare the true 5-year cost of owning any two vehicles. Depreciation, fuel, insurance, maintenance, and more — with live EIA prices and Argonne ANL data."
            borderColor="var(--scenario-a-border)"
            hoverBg="var(--nav-bg-personal)"
            onPick={handlePick}
            disabled={submitting}
          />
          <ModeCard
            mode="fleet"
            icon={TruckIcon}
            headline="Fleet TCO"
            body="Model total cost of ownership across your fleet. Vocation-adjusted fuel, Section 179 tax benefit, labor, and cost per mile — built for fleet operators."
            badge="Business"
            borderColor="var(--scenario-b-border)"
            hoverBg="var(--nav-bg-fleet)"
            onPick={handlePick}
            disabled={submitting}
          />
        </div>

        {submitting && slowLoad && (
          <p
            role="status"
            style={{
              textAlign: 'center',
              marginTop: 20,
              fontSize: 13,
              color: 'var(--tco-ink-3)',
            }}
          >
            Connecting to the server — it may be waking up (about a minute)…
          </p>
        )}

        {!isAuthenticated && (
          <footer
            style={{
              textAlign: 'center',
              marginTop: 36,
              fontSize: 14,
              color: 'var(--tco-ink-2)',
            }}
          >
            Already have an account?{' '}
            <Link
              to="/login"
              style={{
                color: 'var(--tco-ink)',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                fontWeight: 500,
              }}
            >
              Sign in
            </Link>
          </footer>
        )}
      </div>
    </div>
  );
}
